import { useState, useEffect, useRef } from "react";
import { DatePicker, Select, Empty, Tooltip, Input, Table, Steps, Button, Space, Spin, InputRef } from "antd";
import moment from "moment";
import { cloneDeep, debounce, remove, isEqual } from "lodash";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";

import Icon from "@assets/icon_config";
import "./dataProcessingReport.scss";
import { formatDisplayDate } from "@utils/helpers";
import WellBeEMPIService from "@services/empiService.service";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
// import Highlighter from "react-highlight-words";

const { Option } = Select;
const { RangePicker } = DatePicker;

const DataProcessingReport = () => {
  const defaultSortConfigBatchTable = { dataIndex: "batch_prcss_start_date", sortOrder: "descend" };
  const nonSortableBatchTableColumn = ["batch_id"];

  const [payerList, setPayerList] = useState([]);
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [payer, setPayer] = useState<any>(null);
  const [searchedFileName, setSearchedFileName] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [batchTableColumnConfig, setBatchTableColumnConfig] = useState([]);
  const [batchTableLoader, setBatchLoader] = useState(true);
  const [pipeLineTableLoader, setPipeLineLoader] = useState(true);
  const [batchListData, setBatchListData] = useState([]);
  const [dataProcessingPipelineTableColumnConfig, setDataProcessingPipelineTableColumnConfig] = useState<any>([]);
  const [dataProcessingPipelineData, setDataProcessingPipelineData] = useState<any>(null);
  const searchInput = useRef<InputRef>(null);
  const [extendedTableExpandedRow, setExtendedTableExpandedRow] = useState<any>([]);
  const [batchTableSort, setBatchTableSort] = useState<any>(defaultSortConfigBatchTable);

  // Data Processing Pipeline Table Column Config
  const handleSearch = (selectedKeys: string[], confirm: (param?: any) => void, dataIndex: any) => {
    confirm();
  };
  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };
  const getColumnSearchProps = (dataIndex: any) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
    onFilter: (value: any, record: any) =>
      record[dataIndex].value
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible: any) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const renderBatchTableCell = (record: any, dataIndex: any, index: any) => {
    const highLightedLinkColumn = ["batch_id"];
    const dateStampColumn = ["batch_prcss_start_date", "batch_prcss_end_date"];

    const className = `dataProcessingReport__body__batch-container__table__batch-cell${highLightedLinkColumn.includes(dataIndex) ? "__link" : ""}`;

    const value = dateStampColumn.includes(dataIndex) ? formatDisplayDate(record?.[dataIndex], "reminderDateActualFormat", "dateTimeColumnFilterDisplayFormatWithTime", true) : record?.[dataIndex];
    return (
      <div
        className={className}
        onClick={() => {
          if (highLightedLinkColumn.includes(dataIndex)) {
            setSelectedBatch(record);
            fetchDataProcessingPipeLineTableData(record?.batch_id, searchedFileName);
            setExtendedTableExpandedRow([]);
          }
        }}
      >
        {value}
      </div>
    );
  };

  const renderPipeLineCell = (record: any, dataIndex: any, index: any) => {
    const text_left_align = ["payorName", "fileGroupName"];
    const text_no_background = ["payorName", "fileGroupName"];
    const hideToolTip = ["payorName", "fileGroupName"];
    const formatNumberColumn = ["rawLayer", "cleansedLayer", "empiMapping"];

    const cellToolTipData = () => {
      return (
        <div className="dataProcessingReport__body__data-processing-pipeline-container__table__cell__tooltip-container__body">
          <div className="dataProcessingReport__body__data-processing-pipeline-container__table__cell__tooltip-container__body__status">{record?.[dataIndex]?.status}</div>
          <div className="dataProcessingReport__body__data-processing-pipeline-container__table__cell__tooltip-container__body__dateTime">
            {record?.[dataIndex]?.dateTimeStamp && record?.[dataIndex]?.dateTimeStamp !== "NA"
              ? formatDisplayDate(record?.[dataIndex]?.dateTimeStamp, "reminderDateActualFormat", "dateTimeColumnFilterDisplayFormatWithTime", true)
              : null}
          </div>
        </div>
      );
    };

    let className = `dataProcessingReport__body__data-processing-pipeline-container__table__cell 
    ${text_left_align.includes(dataIndex) ? "table-cell__left-align" : ""} 
    ${!record || !record?.[dataIndex]?.colorCode ? "table-cell__empty-data" : `table-cell__color__${record?.[dataIndex]?.colorCode} `}
    ${text_no_background.includes(dataIndex) ? "table-cell__no-background" : ""}
    `;

    const showToolTip = hideToolTip?.includes(dataIndex);

    const fileNameToolTip = () => {
      return <div>{record?.[dataIndex]?.value}</div>;
    };

    const value = formatNumberColumn.includes(dataIndex) ? Number(record?.[dataIndex]?.value)?.toLocaleString() : record?.[dataIndex]?.value;

    return (
      <div className={className}>
        {record ? (
          <Tooltip title={showToolTip ? fileNameToolTip() : cellToolTipData()} overlayClassName={"dataProcessingReport__body__data-processing-pipeline-container__table__cell__tooltip-container"}>
            <div className={`dataProcessingReport__body__data-processing-pipeline-container__table__cell__overflow-handler ${record?.[dataIndex]?.value === "NO" ? "s" : ""}`}>{value}</div>
          </Tooltip>
        ) : null}
      </div>
    );
  };

  //////////////////////////////////////////////

  // On Filter Change Handler
  const onDateChange = (e: any) => {
    setStartDate(moment(e[0]));
    setEndDate(moment(e[1]));
    onSubmitFilter(moment(e[0]), moment(e[1]), payer, searchedFileName);
  };

  const onPayerChange = (newPayer: any) => {
    setPayer(newPayer);
    onSubmitFilter(startDate, endDate, newPayer, searchedFileName);
  };

  // const debouncedFileNameSearch = debounce((inputString) => onSubmitFilter(startDate, endDate, payer, inputString), 500);

  // const onFileNameChange = (inputString: any) => {

  // };

  /////////////////////////////////////////
  // API Service Call
  /////////////////////////////////////////

  // Fetch DropDown detail of the report
  const fetchPayerDropDown = () => {
    WellBeEMPIService.getPayersList().subscribe({
      next: (response: any) => {
        let tempPayerList = response.data.data.payers?.map((item: any) => item?.name);
        tempPayerList?.unshift("All Payors");
        setPayerList(tempPayerList);
        if (tempPayerList?.length > 0) {
          setPayer(tempPayerList?.[0]);
          fetchBatchTableData(startDate, endDate, tempPayerList?.[0], searchedFileName);
        }
      },
      error: (error) => {
        console.log(error);
        setBatchLoader(false);
      },
    });
  };

  // Fetch Data Processing pipeline Column Config
  const fetchDataPipeLineColumnConfig = () => {
    WellBeEMPIService.fetchAttributeDropdown("data_processing_pipeLine_table_colConfig").subscribe({
      next: (response: any) => {
        const tempColConfig = response.data.data.data?.map((item: any) => {
          let column: any = cloneDeep(item);
          const backgroundColoredColumn = ["rawLayer", "cleansedLayer", "empiMapping", "fileAvailability"];

          //  this config is for right hand side table
          return {
            ...column,
            title: column?.label,
            ellipsis: true,
            textWrap: "word-break",
            width: 150,
            key: column?.dataIndex,
            ...(column?.searchFilter === "Y" ? getColumnSearchProps(column?.dataIndex) : {}),
            render: (text: any, record: any, index: any) => {
              return renderPipeLineCell(record, column?.dataIndex, index);
            },
            className: backgroundColoredColumn.includes(column?.dataIndex) ? "p0" : "",
          };
        });
        setDataProcessingPipelineTableColumnConfig(tempColConfig);
      },
      error: (error) => {
        console.log(error);
      },
    });
  };

  const handleBatchTableChange: any = (pagination: any, filters: any, sorter: any) => {
    let sortObject: any = {};
    let sortedBatchList: any = batchListData;

    if (sorter?.columnKey === defaultSortConfigBatchTable.dataIndex && !sorter?.order) {
      sortObject = { ...defaultSortConfigBatchTable, sortOrder: "ascend" };
    } else if ((sorter?.columnKey === batchTableSort.dataIndex && batchTableSort.sortOrder === sorter?.order) || (sorter?.columnKey === batchTableSort.dataIndex && !sorter?.order)) {
      sortObject = defaultSortConfigBatchTable;
    } else {
      sortObject = { dataIndex: sorter?.columnKey, sortOrder: sorter?.order };
    }

    if (!isEqual(sortObject, batchTableSort)) {
      setBatchTableSort(sortObject);

      if (batchListData?.length > 0) {
        let sortedBatchList: any = [];
        if (sortObject.sortOrder === "ascend") {
          sortedBatchList = batchListData.sort(function (a: any, b: any) {
            return a?.[sortObject?.dataIndex]?.localeCompare(b?.[sortObject?.dataIndex]);
          });
        } else {
          sortedBatchList = batchListData.sort(function (a: any, b: any) {
            return b?.[sortObject?.dataIndex]?.localeCompare(a?.[sortObject?.dataIndex]);
          });
        }
        setBatchListData(sortedBatchList);
        fetchDataProcessingPipeLineTableData(sortedBatchList?.[0]?.batch_id, searchedFileName);
        setSelectedBatch(sortedBatchList?.[0]);
      }
    } else if (pagination.current !== page) {
      const fetchBatch = pagination.current * pageSize - pageSize;
      fetchDataProcessingPipeLineTableData(sortedBatchList?.[fetchBatch]?.batch_id, searchedFileName);
      setSelectedBatch(sortedBatchList?.[fetchBatch]);
    }
  };

  // Fetch Batch Table column Config
  const fetchBatchTableColumnConfig = () => {
    WellBeEMPIService.getColumnsList({ pageName: "Pipeline-Batches" }).subscribe({
      next: (response: any) => {
        setBatchTableColumnConfig(response.data.data.columns);
      },
      error: (error: any) => {
        console.log(error);
      },
    });
  };

  const configedBatchColumn = batchTableColumnConfig?.map((item: any) => {
    let column = cloneDeep(item);
    return {
      ...column,
      title: column?.displayName,
      key: column?.dataIndex,
      ...(!nonSortableBatchTableColumn.includes(column?.dataIndex) ? { sorter: (a: any, b: any) => a?.[column?.dataIndex]?.localeCompare(b?.[column?.dataIndex]) } : {}),
      sortOrder: batchTableSort.dataIndex === column?.dataIndex ? batchTableSort.sortOrder : null,
      // title: <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}><span>{column?.displayName}</span><span><SortHandler column={column} sortData={batchTableSort} handleSort={handleSort} /></span></div>,
      render: (text: any, record: any, index: any) => {
        return renderBatchTableCell(record, column?.dataIndex, index);
      },
      width: column?.dataIndex === "batch_id" ? 50 : column?.dataIndex === "batch_prcss_status" ? 70 : 100,
    };
  });

  const configedDataProcessingPipelineColumn = dataProcessingPipelineTableColumnConfig?.filter((item: any) => item?.subTableColumn === "N");

  // Fetch Batch Table Data
  const fetchBatchTableData = (fromDate: any, toDate: any, newPayor: any, fileSearchString: any) => {
    const payload = {
      batchStartDate: `${moment(fromDate).format("YYYY-MM-DD")} 00:00:00`,
      batchEndDate: `${moment(toDate).format("YYYY-MM-DD")} 23:59:59`,
      payor: newPayor,
      fileName: fileSearchString,
    };
    setBatchLoader(true);
    setPipeLineLoader(true);
    WellBeEMPIService.fetchPipeLineBatchList(payload).subscribe({
      next: (response: any) => {
        const tempBatchListData = response?.data?.data?.records?.map((item: any, index: any) => {
          return {
            ...item,
            uniqueKey: index,
          };
        });
        setBatchListData(tempBatchListData);
        if (tempBatchListData?.length > 0) {
          fetchDataProcessingPipeLineTableData(tempBatchListData?.[0]?.batch_id, fileSearchString);
          setSelectedBatch(tempBatchListData?.[0]);
        } else {
          setDataProcessingPipelineData(null);
          setSelectedBatch(null);
          setPipeLineLoader(false);
        }
        setBatchLoader(false);
      },
      error: (error: any) => {
        console.log(error);
        setBatchLoader(false);
        setPipeLineLoader(false);
        setDataProcessingPipelineData(null);
      },
    });
  };

  // Fetch Data Processing Pipeline Table Data
  const fetchDataProcessingPipeLineTableData = (batchId: any, fileSearchString: any) => {
    setPipeLineLoader(true);
    WellBeEMPIService.fetchDataProcessingPipeLineData(batchId, fileSearchString).subscribe({
      next: (response: any) => {
        const data = response?.data?.data.records?.batchProcessingPipeLineData?.map((item: any, index: any) => {
          let tempData = cloneDeep(item);
          tempData = {
            ...tempData.metaData,
            fileList: tempData.fileNames,
            key: index,
          };
          return tempData;
        });
        setDataProcessingPipelineData({ ...response?.data?.data.records, batchProcessingPipeLineData: data });
        if (fileSearchString && fileSearchString?.length > 0) {
          let tempFoundFileOnString: any = [];
          for (let i = 0; i < data?.length; i++) {
            for (let j = 0; j < data?.[i]?.fileList?.length; j++) {
              if (data?.[i]?.fileList?.[j]?.fileName?.includes(fileSearchString)) {
                tempFoundFileOnString?.push(i);
                break;
              }
            }
            if (tempFoundFileOnString?.length > 0) {
              break;
            }
          }
          if (tempFoundFileOnString?.length > 0) {
            setExtendedTableExpandedRow(tempFoundFileOnString);
          }
        } else {
          setExtendedTableExpandedRow([]);
        }

        setPipeLineLoader(false);
      },
      error: (error: any) => {
        console.log(error);
        setPipeLineLoader(false);
      },
    });
  };

  /////////////////////////////////

  useEffect(() => {
    fetchBatchTableColumnConfig();
    fetchDataPipeLineColumnConfig();
    fetchPayerDropDown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitFilter = (fromDate: any, toDate: any, newPayer: any, searchFileNameString: any) => {
    setDataProcessingPipelineData(null);
    setSelectedBatch(null);
    setExtendedTableExpandedRow([]);
    setBatchTableSort(defaultSortConfigBatchTable);
    fetchBatchTableData(fromDate, toDate, newPayer, searchFileNameString);
    // setDataProcessingPipelineTableColumnConfig(resetBatchTableColumnConfig);
  };

  // Batch Timeline steps ToolTip
  const getToolTipForBatchInfo = (data: any) => {
    return (
      <div className="dataProcessingBatchInfo-tooltip__container">
        <div>{data?.status}</div>
        {data?.dateTimeStamp && data?.dateTimeStamp !== "NA" ? (
          <div>{formatDisplayDate(data?.dateTimeStamp, "reminderDateActualFormat", "dateTimeColumnFilterDisplayFormatWithTime", true)}</div>
        ) : null}
      </div>
    );
  };

  const expandedRowRender = (detailedRecord: any, detailedIndex: any) => {
    // let columns: TableColumnsType<any> = [
    //   { title: "File Name", dataIndex: "fileName", key: "fileName" },
    //   { title: "File Received Date", dataIndex: "fileReceivedDateTime", key: "fileReceivedDateTime" },
    // ];

    let columns: any = dataProcessingPipelineTableColumnConfig?.filter((item: any) => item?.subTableColumn === "Y");

    const renderExtendedTableCell = (extendedRecord: any, extendedIndex: any, extendedDataIndex: any) => {
      const extendedTableLeftAlignedCell = ["fileName"];
      const extendedTableDateFormatCell = ["fileReceivedDateTime"];
      let extendedTableClassName = `dataProcessingReport__body__data-processing-pipeline-container__table__extended-pipeline-table__cell 
      ${extendedTableLeftAlignedCell.includes(extendedDataIndex) ? "left-align" : ""}`;
      const value = extendedTableDateFormatCell.includes(extendedDataIndex)
        ? formatDisplayDate(extendedRecord?.[extendedDataIndex], "reminderDateActualFormat", "dateTimeColumnFilterDisplayFormatWithTime", true)
        : extendedRecord?.[extendedDataIndex];
      return (
        <div className={extendedTableClassName}>
          <div>{value}</div>
        </div>
      );
    };

    columns = columns?.map((item: any) => {
      return {
        ...item,
        width: 150,
        render: (text: any, record1: any, index1: any) => renderExtendedTableCell(record1, index1, item?.dataIndex),
      };
    });

    return (
      <div className="dataProcessingReport__body__data-processing-pipeline-container__table__extended-pipeline-table">
        <Table columns={columns} dataSource={detailedRecord?.fileList} pagination={false} />
      </div>
    );
  };

  const onPipeLineTableRowExpand = (expand: any, record: any) => {
    let tempExtendedTableExpandedRow = cloneDeep(extendedTableExpandedRow);
    const selectedRecord = record.key;
    if (expand) {
      tempExtendedTableExpandedRow.push(selectedRecord);
    } else {
      remove(tempExtendedTableExpandedRow, (item: any) => item === selectedRecord);
    }
    setExtendedTableExpandedRow(tempExtendedTableExpandedRow);
  };

  const expandCollapsAllRow = () => {
    if (extendedTableExpandedRow?.length > 0) {
      setExtendedTableExpandedRow([]);
    } else {
      setExtendedTableExpandedRow(dataProcessingPipelineData?.batchProcessingPipeLineData?.map((item: any) => item?.key));
    }
  };

  return (
    <div className="dataProcessingReport">
      {/* <div className="dataProcessingReport__header"> */}
      {/* <div className="dataProcessingReport__header__title">Data Processing Pipeline Report</div> */}
      {/* </div> */}
      <div className="dataProcessingReport__body">
        <div className="dataProcessingReport__body__batch-container">
          <div className="dataProcessingReport__body__batch-container__filter">
            <div className="dataProcessingReport__body__batch-container__filter__dropdown">
              <Select value={payer} size={"large"} onChange={onPayerChange}>
                {payerList?.map((item: any, index: any) => {
                  return (
                    <Option key={item + index} value={item}>
                      {item}
                    </Option>
                  );
                })}
              </Select>
            </div>
            <div className="dataProcessingReport__body__batch-container__filter__dateRange">
              <RangePicker
                size="large"
                value={[startDate, endDate]}
                format={"MM-DD-YYYY"}
                onChange={onDateChange}
                separator={<span className="range-picker-separator-members-list">-</span>}
                picker="date"
                allowClear={false}
                disabledDate={(current) => moment(current.format("YYYY-MM-DD")) > moment(moment().format("YYYY-MM-DD"))}
                ranges={{
                  Today: [moment(`${moment().format("YYYY-MM-DD")} 00:00:00`), moment(`${moment().format("YYYY-MM-DD")} 23:59:59`)],
                  Yesterday: [moment(`${moment().format("YYYY-MM-DD")} 00:00:00`).subtract(1, "days"), moment(`${moment().subtract(1, "days").format("YYYY-MM-DD")} 23:59:59`)],
                  "Last 7 Days": [moment().subtract(7, "days"), moment()],
                }}
              />
            </div>
          </div>
          <div className="dataProcessingReport__body__batch-container__filter">
            <div className="dataProcessingReport__body__batch-container__filter__input-string">
              <Input
                placeholder="File Name"
                // value={searchedFileName}
                defaultValue={searchedFileName}
                onChange={debounce((e) => {
                  setSearchedFileName(e.target.value);
                  onSubmitFilter(startDate, endDate, payer, e.target.value);
                }, 500)}
                prefix={<Icon name="patientProfileNameSearch" />}
              />
            </div>
          </div>

          {/* <div className="dataProcessingReport__body__batch-container__search-button">
            <Button type="primary" onClick={() => onSubmitFilter()}>
              Search
            </Button>
          </div> */}
          <div className="dataProcessingReport__body__batch-container__table">
            <div className="dataProcessingReport__body__batch-container__table__header">
              Available Batch List{" "}
              <span className="dataProcessingReport__body__batch-container__table__header__extraInfo">
                {" "}
                ( Showing Info as per {moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()} timezone. )
              </span>
            </div>

            <Spin spinning={batchTableLoader} size={"large"} indicator={<HealthemSpinner size={30} />}>
              <Table
                columns={configedBatchColumn}
                dataSource={batchListData}
                scroll={{ y: window.innerHeight - 470 }}
                onChange={(pagination, filters, sorter) => {
                  handleBatchTableChange(pagination, filters, sorter);
                }}
                pagination={{
                  defaultPageSize: 1,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "15", "20", "25", "30"],
                  pageSize: pageSize,
                  current: page,
                  onChange: (pageNumber, pageSizeNumber) => {
                    setPage(pageNumber);
                    setPageSize(pageSizeNumber);
                  },
                }}
                rowClassName={(record: any, index: any) => (record?.uniqueKey === selectedBatch?.uniqueKey ? "dataProcessingReport__body__batch-container__table__selected-row" : "")}
              />
            </Spin>
          </div>
        </div>
        <div className="dataProcessingReport__body__data-processing-pipeline-container">
          <Spin spinning={pipeLineTableLoader} size={"large"} indicator={<HealthemSpinner size={42} />}>
            {selectedBatch && dataProcessingPipelineData?.batchProcessingPipeLineData?.length > 0 ? (
              <>
                <div className="dataProcessingReport__body__data-processing-pipeline-container__batch-title">
                  <span className="dataProcessingReport__body__data-processing-pipeline-container__batch-title__value">{`Batch: ${selectedBatch?.batch_id}`}</span>
                </div>
                <div className="dataProcessingReport__body__data-processing-pipeline-container__progress-status">
                  <Steps
                    size="small"
                    labelPlacement="vertical"
                    items={dataProcessingPipelineData?.batch?.map((item: any) => {
                      let tempItem = {};
                      tempItem = {
                        title: (
                          <Tooltip overlayClassName="dataProcessingBatchInfo-tooltip" title={item?.status ? getToolTipForBatchInfo(item) : "NA"}>
                            {item?.label}
                          </Tooltip>
                        ),
                        status: item?.status === "Successful" ? "finish" : item?.status === "Failed" ? "error" : "wait",
                        ...(item?.status !== "Failed"
                          ? {
                              icon:
                                item?.status === "NA" || !item?.status ? (
                                  <CloseCircleOutlined style={{ fontSize: "42px" }} />
                                ) : (
                                  <Icon name="program-status" colorOfBrand={item?.status === "Successful" ? "#1BBE75" : item?.status === "Warning" ? "#f2960d" : "#dfe1e6"} width={42} height={42} />
                                ),
                            }
                          : {
                              icon: <CloseCircleOutlined style={{ fontSize: "42px", color: "#E75547" }} />,
                            }),
                      };

                      return tempItem;
                    })}
                  />
                </div>
                <div className="dataProcessingReport__body__data-processing-pipeline-container__table">
                  <div className="dataProcessingReport__body__data-processing-pipeline-container__table__header-2">
                    <div className="dataProcessingReport__body__data-processing-pipeline-container__table__header-2__collapseAllHandler" onClick={() => expandCollapsAllRow()}>
                      <span>{extendedTableExpandedRow?.length > 0 ? "Collapse All Rows" : "Expand All Rows"}</span>
                      <span>
                        <Icon name={`${extendedTableExpandedRow?.length > 0 ? "arrowUp" : "arrowDown"}`} colorOfBrand={"#0072E3"} />
                      </span>
                    </div>
                  </div>
                  <Table
                    scroll={{ x: true, y: window.innerHeight - 440 }}
                    columns={configedDataProcessingPipelineColumn}
                    dataSource={dataProcessingPipelineData?.batchProcessingPipeLineData}
                    pagination={false}
                    expandable={{ expandedRowRender, expandedRowKeys: extendedTableExpandedRow, onExpand: onPipeLineTableRowExpand }}
                    bordered={true}
                    tableLayout="fixed"
                  />
                </div>
              </>
            ) : (
              <div className="dataProcessingReport__body__data-processing-pipeline-container__no-data">
                <Empty />
              </div>
            )}
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingReport;
